import { Add } from '@mui/icons-material';
import { Button, styled } from '@mui/material';
import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { PageHeaderStatic, WidgetIcon } from '@procureai/ui-mui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { TenantDomainType } from '~/common/types';
import { DataGridQueryBuilder, useDataGridQueryBuilderQuery } from '~/components/DataGridQueryBuilder';
import { DomainIcon } from '~/components/DomainIcon/DomainIcon';
import { Filters, FilterSaveStatus, useFilters } from '~/components/Filters';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { pageLayoutClasses } from '~/components/PageLayout/PageLayout.styled';
import { useOpenClose } from '~/hooks';
import { SavingsWidget } from '~/modules/SavingsWidget/SavingsWidget';
import { RouteId } from '~/routes/RouteId';

import { useSavingsDataGridQueryBuilderConfig } from './Savings.queryBuilder';

const domain = TenantDomainType.SAVINGS_LIST;

const StyledPageLayout = styled(PageLayout)(() => ({
  [`& > .${pageLayoutClasses.content}.${pageLayoutClasses.fullHeight}`]: {
    minHeight: 0,
  },
}));

export const Savings = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const {
    queryId,
    query,
    onQueryChange,
    onQueryIdChange,
    rulesTotal,
    loading: queryLoading,
  } = useDataGridQueryBuilderQuery({ domain });
  const { onFilterSave, saveStatus, selected, ...filtersProps } = useFilters({ domain, queryId });
  const [createDialogProps, handleOpenCreateDialog] = useOpenClose();

  const { id: selectedSavingId = null } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { search } = useLocation();

  const handleSelectedSavingIdChange = useCallback(
    (id: string | null) => {
      // Keep the encoded filter query (?q=...) when toggling the deep link.
      navigate({ pathname: id ? RouteId.savingDetail(id) : RouteId.savings, search });
    },
    [navigate, search],
  );

  return (
    <StyledPageLayout
      fullHeight
      renderHeader={
        <>
          <PageHeaderStatic
            title={t('Savings.title')}
            maxWidth={false}
            sx={{ flexShrink: 0 }}
            icon={<WidgetIcon icon={<DomainIcon domain={domain} />} />}
            right={
              <div className="flex items-center gap-3">
                <Filters
                  {...filtersProps}
                  selected={selected}
                  onFilterSelect={(filter) => {
                    onQueryIdChange(filter.queryId);
                  }}
                />
                {hasPermission(Permission.CmdiSavingEdit) ? (
                  <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreateDialog}>
                    {t('Savings.actions.create')}
                  </Button>
                ) : null}
              </div>
            }
          />
          <DataGridQueryBuilder
            query={query}
            clearDisabled={rulesTotal === 0}
            saveDisabled={saveStatus === FilterSaveStatus.DISABLED}
            onQueryChange={onQueryChange}
            onSave={onFilterSave}
            selectedFilterName={selected?.name}
            useConfig={useSavingsDataGridQueryBuilderConfig}
          />
        </>
      }
    >
      <SavingsWidget
        query={query}
        queryLoading={queryLoading}
        selectedSavingId={selectedSavingId}
        onSelectedSavingIdChange={handleSelectedSavingIdChange}
        createDialogProps={{
          open: createDialogProps.open,
          onClose: createDialogProps.onClose,
        }}
      />
    </StyledPageLayout>
  );
};
